import { type FormEvent, useEffect, useState } from "react";

interface UseDebouncedSearchOptions<TEmpty extends string | null> {
	emptyValue: TEmpty;
	delayMs?: number;
}

interface UseDebouncedSearchReturn<TEmpty extends string | null> {
	inputValue: string;
	filter: string | TEmpty;
	handleInputChange: (value: string) => void;
	handleSearch: (event: FormEvent) => void;
}

export const useDebouncedSearch = <TEmpty extends string | null = string>({
	emptyValue,
	delayMs = 1000,
}: UseDebouncedSearchOptions<TEmpty>): UseDebouncedSearchReturn<TEmpty> => {
	const [inputValue, setInputValue] = useState<string>("");
	const [filter, setFilter] = useState<string | TEmpty>(emptyValue);

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setFilter(inputValue.trim() === "" ? emptyValue : inputValue);
		}, delayMs);

		return () => clearTimeout(timeoutId);
	}, [inputValue, emptyValue, delayMs]);

	const handleInputChange = (value: string) => {
		setInputValue(value);
		if (value.trim() === "") {
			setFilter(emptyValue);
		}
	};

	const handleSearch = (event: FormEvent) => {
		setFilter(inputValue);
		event.preventDefault();
	};

	return {
		inputValue,
		filter,
		handleInputChange,
		handleSearch,
	};
};
